import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import type { Client, User } from './UserService';

const resolveFirebaseUid = (user: User | null): string | null =>
  auth.currentUser?.uid || user?.firebase_uid || null;

const resolveActiveClientId = (
  user: User | null,
  selectedClient: Client | null,
): string | null => selectedClient?.clientId || user?.current_client_id || null;

const resolveActiveRole = (
  user: User | null,
  selectedClient: Client | null,
): string | null => selectedClient?.userRole || user?.userRole || null;

export class FirebaseUserContextService {
  static async syncAuthContext(
    user: User | null,
    selectedClient: Client | null,
  ): Promise<void> {
    const firebaseUid = resolveFirebaseUid(user);

    if (!user || !firebaseUid) {
      return;
    }

    const activeClientId = resolveActiveClientId(user, selectedClient);
    const activeRole = resolveActiveRole(user, selectedClient);

    const payload = {
      uid: firebaseUid,
      firebase_uid: firebaseUid,
      userId: user.userId,
      email: user.userEmail ?? auth.currentUser?.email ?? null,
      userEmail: user.userEmail ?? auth.currentUser?.email ?? null,
      displayName: user.userDisplayName ?? auth.currentUser?.displayName ?? null,
      userDisplayName: user.userDisplayName ?? auth.currentUser?.displayName ?? null,
      role: activeRole,
      userRole: activeRole,
      current_client_id: activeClientId,
      clientId: activeClientId,
      tenantId: activeClientId,
      userGlobalRole: user.userGlobalRole ?? null,
      lastContextSyncAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    try {
      await setDoc(doc(db, 'users', firebaseUid), payload, { merge: true });
    } catch (error) {
      console.error('[FirebaseUserContextService] Error syncing user context:', error);
      throw error;
    }
  }
}
